import classNames from 'classnames';
import Typography from '@/components/atoms/typography/typography';
import Pill from '@/components/molecules/pill/pill';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import styles from './configuration-flow-step-indicator.module.scss';

interface ConfigurationFlowStepIndicatorProps {
  /** Additional class names */
  className?: string;
  /** The index of the current step */
  currentStep: number;
  /** The steps of the configuration flow */
  steps: Content.Components['ConfigurationFlowModuleDto']['steps'];
}

const ConfigurationFlowStepIndicator = ({
  className,
  currentStep,
  steps,
}: ConfigurationFlowStepIndicatorProps) => {
  const { stepSummaries } = useHotelContext();

  const hasDivider = (stepIndex: number): boolean => {
    return stepIndex > 0 && stepIndex < steps.length;
  };

  const isActive = (stepIndex: number): boolean => {
    return stepIndex <= currentStep - 1;
  };

  const isCompleted = (stepIndex: number): boolean => {
    return stepIndex < currentStep - 1;
  };

  return (
    <div className={classNames(styles.component, className)}>
      {steps.map((step, stepIndex) => (
        <section key={stepIndex} className={styles.section}>
          <div className={styles.topSection}>
            {hasDivider(stepIndex) && (
              <div
                className={classNames(styles.divider, {
                  [styles.isCompleted]:
                    isActive(stepIndex) || isCompleted(stepIndex),
                })}
              />
            )}

            <Pill
              count={stepIndex + 1}
              icon={isCompleted(stepIndex) ? 'CheckMark' : undefined}
              variant={
                isActive(stepIndex) || isCompleted(stepIndex)
                  ? 'secondary'
                  : 'primary'
              }
            />
          </div>

          <div className={styles.bottomSection}>
            <Typography className={styles.text} tagStyle="bodyMediumBold">
              {step.stepHeadline}
            </Typography>

            {stepSummaries?.[stepIndex] && (
              <Typography className={styles.description} tagStyle="bodySmall">
                {stepSummaries[stepIndex]}
              </Typography>
            )}
          </div>
        </section>
      ))}
    </div>
  );
};

ConfigurationFlowStepIndicator.displayName = 'ConfigurationFlowStepIndicator';

export default ConfigurationFlowStepIndicator;
